<script setup lang="ts">
import {
    compile,
    onBeforeMount,
    onBeforeUnmount,
    onMounted,
    onUnmounted,
    ref,
    useTemplateRef,
    type Component,
} from 'vue';
import { TooltipProvider } from 'reka-ui';
import * as FormComponents from '@/lib/form.ts';
import type { SheetProps } from '@/types';
import type { Workflow } from '@/types/api';

const { workflow, close } = defineProps<
    SheetProps & {
        workflow: Workflow;
    }
>();

const container = useTemplateRef('container');
const form = ref<HTMLFormElement>();

let tags: Element[] = [];
let scriptLoadPromises: Promise<void>[] = [];

onBeforeMount(() => {
    for (const cssFile of workflow.css) {
        const link = document.createElement('link');
        link.setAttribute('href', cssFile);
        link.setAttribute('rel', 'stylesheet');

        document.head.appendChild(link);
        tags.push(link);
    }

    for (const jsFile of workflow.js) {
        const script = document.createElement('script');

        scriptLoadPromises.push(
            new Promise<void>((resolve) => {
                script.addEventListener('load', () => resolve());
                script.addEventListener('error', () => resolve());
            })
        );

        script.setAttribute('src', jsFile);
        script.setAttribute('type', 'module');

        document.body.appendChild(script);
        tags.push(script);
    }
});

onMounted(async () => {
    await Promise.all(scriptLoadPromises);

    form.value = container.value?.querySelector('form') as HTMLFormElement;

    // Formie lazy-inits via IntersectionObserver when loaded dynamically, which can
    // miss the form if it wasn't in the DOM at the time. Force immediate init instead.
    // initForms(false) is safe to call multiple times — initForm() deduplicates by formHashId.
    (window as any).Formie?.initForm(form.value);

    form.value.addEventListener('onAfterFormieSubmit', (e) => {
        const { isFinalPage, success } = (
            e as CustomEvent<{ isFinalPage: boolean; success: boolean }>
        ).detail;

        if (isFinalPage && success) {
            close();
        }
    });
});

onBeforeUnmount(() => {
    (window as any).Formie?.destroyForm(form.value);
});

onUnmounted(() => {
    for (const tag of tags) {
        tag.remove();
    }
});

const dynamicComponent: Component = {
    render: compile(workflow.html),
    components: {
        TooltipProvider,
        ...FormComponents,
    },
};
</script>

<template>
    <div
        ref="container"
        class="-mx-4 flex h-full flex-1 flex-col"
    >
        <component :is="dynamicComponent" />
    </div>
</template>
